import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, ShieldCheck, Truck, MessageCircle } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { ProductCard } from "../../components/shop/ProductCard";
import { shopApi } from "../../lib/shopApi";

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [config, setConfig] = useState(null);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    shopApi.get("/shop/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    shopApi
      .get("/shop/products", { params: { category, sort } })
      .then((r) => {
        setProducts(r.data.products);
        setCategories(r.data.categories);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, sort]);

  return (
    <ShopLayout title="Shop Loc Care | Loctician Carlocs Johannesburg">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#C9A24B]/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,162,75,0.10),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 text-center">
          {config?.shop_announcement && (
            <p data-testid="shop-announcement" className="inline-block border border-[#C9A24B]/40 text-[#C9A24B] text-xs uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              {config.shop_announcement}
            </p>
          )}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Crown className="w-8 h-8 text-[#C9A24B] mx-auto mb-5" />
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5">
              The Crown <span className="italic text-[#C9A24B]">Collection</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* FILTERS + GRID */}
      <section className="max-w-7xl mx-auto px-5 pb-16" data-testid="products-section">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            onClick={() => setCategory("all")}
            data-testid="filter-category-all"
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors duration-300 ${category === "all" ? "bg-[#C9A24B] text-[#1C1C1C] border-[#C9A24B]" : "border-[#C9A24B]/25 text-[#F5EFE3]/60 hover:border-[#C9A24B]/60"}`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors duration-300 ${category === c ? "bg-[#C9A24B] text-[#1C1C1C] border-[#C9A24B]" : "border-[#C9A24B]/25 text-[#F5EFE3]/60 hover:border-[#C9A24B]/60"}`}
            >
              {c}
            </button>
          ))}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sort-select"
            className="ml-auto bg-[#232323] border border-[#C9A24B]/25 rounded-full px-4 py-2 text-xs uppercase tracking-widest text-[#F5EFE3]/70 focus:outline-none focus:border-[#C9A24B]"
          >
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>

        {loading ? (
          <div className="py-20 text-center text-[#F5EFE3]/40 text-sm" data-testid="products-loading">Loading products…</div>
        ) : products.length === 0 ? (
          <div className="py-20 text-center text-[#F5EFE3]/40 text-sm" data-testid="products-empty">No products found for this filter.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* TRUST STRIP */}
      <section className="border-t border-[#C9A24B]/10 bg-[#181818]" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-5 py-12 grid sm:grid-cols-2 gap-8 text-center max-w-4xl">
          <div>
            <ShieldCheck className="w-6 h-6 text-[#C9A24B] mx-auto mb-3" />
            <p className="font-serif text-lg mb-1">Secure Payment</p>
            <p className="text-[#F5EFE3]/45 text-sm font-light">Secure card payments via iKhokha — your details stay safe.</p>
          </div>
          <div>
            <Truck className="w-6 h-6 text-[#C9A24B] mx-auto mb-3" />
            <p className="font-serif text-lg mb-1">Delivery Across South Africa</p>
            <p className="text-[#F5EFE3]/45 text-sm font-light">Paxi nationwide in 3-5 days, same-day Uber in Johannesburg, or free salon pick up.</p>
          </div>
        </div>
      </section>

      {/* WHATSAPP HELP */}
      {config?.whatsapp && (
        <section className="max-w-7xl mx-auto px-5 py-12 text-center">
          <p className="text-[#F5EFE3]/50 text-sm mb-4">Need help choosing the right product?</p>
          <a
            href={`https://wa.me/${config.whatsapp}?text=${encodeURIComponent("Hi Carlocs! I need help choosing a product from the shop.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-help-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-[#C9A24B] text-[#1C1C1C] px-10 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(201,162,75,0.35)] transition-all duration-300"
          >
            <MessageCircle className="w-4 h-4" /> Order Help on WhatsApp
          </a>
        </section>
      )}
    </ShopLayout>
  );
};

export default ShopPage;
